'use client';

// Compact run ledger for a single agent — the last N wakes with an outcome
// icon, summary, credits, and relative time. Fetched lazily by the card when
// the user expands it (keeps the grid GET cheap).

import { useEffect, useState } from 'react';
import {
  Circle,
  FileText,
  Flag,
  WarningCircle,
  Coins,
  Spinner,
} from '@phosphor-icons/react';
import type { AgentRun, RunOutcome } from './types';
import { relativeTime } from './relative-time';
import { AgentRunDetail } from './agent-run-detail';

function OutcomeIcon({ outcome, status }: { outcome: RunOutcome | null; status: string }) {
  if (status === 'running') {
    return <Spinner weight="bold" className="size-3.5 shrink-0 animate-spin text-violet-400" />;
  }
  switch (outcome) {
    case 'report':
      return <FileText weight="duotone" className="size-3.5 shrink-0 text-sky-400" />;
    case 'proposal':
      return <Flag weight="duotone" className="size-3.5 shrink-0 text-violet-400" />;
    case 'error':
      return <WarningCircle weight="duotone" className="size-3.5 shrink-0 text-rose-400" />;
    case 'budget_skip':
      return <Coins weight="duotone" className="size-3.5 shrink-0 text-amber-400" />;
    case 'heartbeat_ok':
    default:
      return <Circle weight="fill" className="size-2 shrink-0 text-muted-foreground/50" />;
  }
}

function outcomeLabel(run: AgentRun): string {
  if (run.status === 'running') return 'Running…';
  if (run.status === 'failed') return run.error || 'Run failed';
  switch (run.outcome) {
    case 'report':
      return run.summary || 'Report';
    case 'proposal':
      return run.summary || 'Proposal awaiting review';
    case 'error':
      return run.error || run.summary || 'Error';
    case 'budget_skip':
      return 'Skipped — daily budget reached';
    case 'heartbeat_ok':
      return run.summary || 'All quiet — nothing to report';
    default:
      return run.summary || 'Completed';
  }
}

export function AgentRunsFeed({ agentId, sessionId }: { agentId: string; sessionId?: string | null }) {
  const [runs, setRuns] = useState<AgentRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AgentRun | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/agents/${agentId}/runs?limit=10`);
        if (!res.ok) throw new Error(`Failed to load runs (${res.status})`);
        const data = await res.json();
        if (!cancelled) setRuns(Array.isArray(data.runs) ? data.runs : []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load runs');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  if (error) {
    return <p className="px-1 py-2 text-[12px] text-rose-400">{error}</p>;
  }
  if (runs === null) {
    return (
      <p className="flex items-center gap-1.5 px-1 py-2 text-[12px] text-muted-foreground">
        <Spinner weight="bold" className="size-3.5 animate-spin" /> Loading runs…
      </p>
    );
  }
  if (runs.length === 0) {
    return (
      <p className="px-1 py-2 text-[12px] text-muted-foreground/70">
        No runs yet — wake the agent to see activity here.
      </p>
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-0.5">
        {runs.map((run) => (
          <li key={run.id}>
            <button
              type="button"
              onClick={() => setSelected(run)}
              className="flex w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/40"
            >
              <span className="mt-0.5 flex size-4 items-center justify-center">
                <OutcomeIcon outcome={run.outcome} status={run.status} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-[12.5px] text-foreground/90">
                  <span className="truncate" title={outcomeLabel(run)}>
                    {outcomeLabel(run)}
                  </span>
                  {run.dryRun && (
                    <span className="shrink-0 rounded bg-amber-400/15 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-amber-400">
                      Test
                    </span>
                  )}
                </p>
                <p className="text-[10.5px] text-muted-foreground/70">
                  {relativeTime(run.finishedAt ?? run.startedAt)}
                  {run.trigger === 'manual' ? ' · manual' : run.trigger === 'event' ? ' · event' : ''}
                  {run.creditsSpent > 0 ? ` · ${run.creditsSpent} cr` : ''}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
      <AgentRunDetail
        run={selected}
        sessionId={sessionId ?? null}
        onOpenChange={(open) => { if (!open) setSelected(null); }}
      />
    </>
  );
}
